export const LINE_MEDIC_DEPLOYMENT_REQUIREMENTS = [
  { category: 'Line Medic Pack', label: 'Line Medic Pack' },
  { category: 'Trauma Bag', label: 'Trauma Bag' },
  { category: 'O2 Bag', label: 'O2 Bag' },
  { category: 'AED', label: 'AED' },
  { category: 'Radio Antenna', label: 'Radio Antenna' },
];

export const LINE_EMT_DEPLOYMENT_REQUIREMENTS = [
  { category: 'Line EMT BLS Pack', label: 'Line EMT BLS Pack' },
  { category: 'O2 Bag', label: 'O2 Bag' },
  { category: 'Radio Antenna', label: 'Radio Antenna' },
];

export const OPTIONAL_DEPLOYMENT_GEAR = [
  { category: 'Clipboard', label: 'Clipboard' },
  { category: 'Combi Tool', label: 'Combi Tool' },
  { category: 'Ice Chest', label: 'Ice Chest' },
];

// Clipboard, Combi Tool, and Ice Chest are recommended but optional for every deployment type.
// They intentionally do not limit deployment capacity. More than one Combi Tool may be selected,
// including for REMS deployments; checkout is limited only by individual asset availability/readiness.
// Radios are currently issued as loaners; program radios can be added once acquired.
// EMTF uses the Line EMT core template: BLS Pack + O2 + antenna.
// Line EMTs do not take a Trauma Bag or AED.
export function deploymentRequirementsForType(deploymentType = 'EMPF') {
  return String(deploymentType || '').trim().toUpperCase() === 'EMTF'
    ? LINE_EMT_DEPLOYMENT_REQUIREMENTS
    : LINE_MEDIC_DEPLOYMENT_REQUIREMENTS;
}

export function assetIsDeploymentReady(asset) {
  return Boolean(
    asset
    && asset.active !== false
    && String(asset.ownership || 'program').toLowerCase() !== 'personal'
    && asset.status === 'available'
    && asset.condition === 'ready'
  );
}

function selectedAssetsForIds(assets = [], selectedIds = []) {
  const selected = new Set((Array.isArray(selectedIds) ? selectedIds : [])
    .map(value => String(value || '').trim().toUpperCase())
    .filter(Boolean));
  return (Array.isArray(assets) ? assets : []).filter(asset =>
    selected.has(String(asset?.id || '').trim().toUpperCase())
  );
}

function evaluateGearRows(selectedAssets = [], requirements = []) {
  return requirements.map(requirement => {
    const matching = selectedAssets.filter(asset => asset?.category === requirement.category);
    return {
      ...requirement,
      satisfied: matching.length > 0,
      count: matching.length,
      assetIds: matching.map(asset => String(asset?.id || '').trim()).filter(Boolean),
    };
  });
}

export function evaluateSelectedDeploymentGear(assets = [], selectedIds = [], deploymentType = 'EMPF') {
  const selectedAssets = selectedAssetsForIds(assets, selectedIds);
  const requirements = deploymentRequirementsForType(deploymentType);
  const rows = evaluateGearRows(selectedAssets, requirements);
  const missing = rows.filter(row => !row.satisfied);

  return {
    ready: missing.length === 0,
    rows,
    missing,
    missingLabels: missing.map(row => row.label),
  };
}

export function evaluateRecommendedDeploymentGear(assets = [], selectedIds = []) {
  const selectedAssets = selectedAssetsForIds(assets, selectedIds);
  const rows = evaluateGearRows(selectedAssets, OPTIONAL_DEPLOYMENT_GEAR);
  const missing = rows.filter(row => !row.satisfied);

  return {
    complete: missing.length === 0,
    rows,
    missing,
    missingLabels: missing.map(row => row.label),
  };
}

function buildDeploymentCapacity(assets = [], requirements = []) {
  const rows = requirements.map(requirement => {
    const matching = (Array.isArray(assets) ? assets : []).filter(asset => asset?.category === requirement.category);
    const readyAssets = matching.filter(assetIsDeploymentReady);
    return {
      ...requirement,
      ready: readyAssets.length,
      total: matching.filter(asset => asset?.active !== false && String(asset.ownership || 'program').toLowerCase() !== 'personal').length,
      readyAssetIds: readyAssets.map(asset => String(asset.id || '').trim()).filter(Boolean),
    };
  });

  const capacity = rows.length ? Math.min(...rows.map(row => row.ready)) : 0;
  const limiting = rows.filter(row => row.ready === capacity).map(row => row.label);

  return {
    capacity,
    rows,
    limiting,
    complete: capacity > 0,
  };
}

export function buildLineMedicDeploymentCapacity(assets = []) {
  return buildDeploymentCapacity(assets, LINE_MEDIC_DEPLOYMENT_REQUIREMENTS);
}

export function buildLineEMTDeploymentCapacity(assets = []) {
  return buildDeploymentCapacity(assets, LINE_EMT_DEPLOYMENT_REQUIREMENTS);
}
